"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnsafeMeasurement = exports.Measurement = exports.isSerializedStringFloatMeasurement = exports.isSerializedMeasurement = exports.MAXIMUM_MEASUREMENT_DECIMAL_PRECISION = void 0;
const error_1 = require("./error");
const math_1 = require("./math");
const logger_1 = require("./logger");
/**
 * Enforced maximum decimal precision for measurement values.
 * Exceeding this precision will result in warning logs, and automatic rounding.
 *
 * If you need greater than 7 digits past the decimal point, consider using
 * a different unit (Ex: stop using tonnes and start using kg as the base unit)
 */
exports.MAXIMUM_MEASUREMENT_DECIMAL_PRECISION = 7;
/** Typeguard checking if Measurement input is already a Measurement model instance */
const isMeasurementModel = (input) => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return input instanceof Measurement;
};
/** Typeguard checking if Measurement input is a "safe" serialized measurement type (i.e. w/ safe float handling) */
const isSerializedMeasurement = (input) => {
    return (typeof input.unit === 'string' &&
        math_1.isSafeFloat(input.value) &&
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        !(input instanceof Measurement));
};
exports.isSerializedMeasurement = isSerializedMeasurement;
/** Typeguard checking if Measurement input is a "safe" serialized measurement type (i.e. w/ safe float handling) */
const isSerializedStringFloatMeasurement = (input) => {
    return (typeof input.unit === 'string' &&
        typeof input.value === 'string' &&
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        !(input instanceof Measurement));
};
exports.isSerializedStringFloatMeasurement = isSerializedStringFloatMeasurement;
/**
 * Utility class for handling any "Measurement" value (where "measurement" means a value that must be
 * paired with a unit to have any meaning -- e.g. weight(10kg), crop quantity(5000 bushels), area (2.5ha), etc.)
 *
 * The Measurement class is de-serialized "smart" model version, with built in safe math functions, precision adjustment,
 * matching units validations, and other tooling, with SerializedMeasurement being the expected "safe" JSON representation.
 *
 * NOTE: Currency is a special exception with its own dedicated tooling.
 * */
class Measurement {
    /**
     * Creates a `Measurement` instance from an existing instance, or from a `SerializedMeasurement`.
     *
     * **CAUTION:** enforces a maximum (reasonable) decimal precision
     *
     * NOTE: Always normalizes `unit` to ALL_CAPS
     */
    constructor(input) {
        let incomingUnit;
        let incomingValueModel;
        if (isMeasurementModel(input)) {
            incomingUnit = input._unit;
            incomingValueModel = input._value;
        }
        else if (exports.isSerializedMeasurement(input) || exports.isSerializedStringFloatMeasurement(input)) {
            incomingUnit = input.unit;
            incomingValueModel = new math_1.SmartFloat(input.value);
        }
        else {
            throw new error_1.CommonUtilsError('Unrecognized input format to Measurement constructor', { input });
        }
        if (!incomingUnit) {
            // NOTE: this intentionally also blocks shenanigans where consumer tries to use empty string as unit input
            throw new error_1.CommonUtilsError('Measurement constructor requires non-empty string value for unit', { input });
        }
        if (!incomingValueModel) {
            throw new error_1.CommonUtilsError('Measurement constructor failed to extract incoming value from input', { input });
        }
        // normalize unit string (e.g. force to ALL_CAPS)
        this._unit = Measurement._normalizeUnit(incomingUnit);
        // enforce maximum decimal precision
        this._value = Measurement._enforceMaximumPrecision(incomingValueModel, this._unit);
    }
    /** Shared bottleneck normalization of unit string (e.g. forcing uppercase) */
    static _normalizeUnit(unit) {
        const normalized = unit.toUpperCase();
        if (unit !== normalized) {
            // log for record-keeping if we actually changed any values
            logger_1.loggerWrapper.info(`Normalized Measurement 'unit' value from "${unit}" to "${normalized}"`);
        }
        return normalized;
    }
    /** Convenience method for use within class to save performance when we already have a SmartFloat model */
    static _enforceMaximumPrecision(value, unit) {
        const incomingDecimalPrecision = value.getDecimalPrecision();
        if (incomingDecimalPrecision > exports.MAXIMUM_MEASUREMENT_DECIMAL_PRECISION) {
            const roundedValue = value.toPrecision(exports.MAXIMUM_MEASUREMENT_DECIMAL_PRECISION);
            const warningData = {
                unit,
                originalValue: value.toNumber(),
                originalDecimalPrecision: incomingDecimalPrecision,
                enforcedMaximumDecimalPrecision: exports.MAXIMUM_MEASUREMENT_DECIMAL_PRECISION,
                roundedValueTo: roundedValue.toNumber()
            };
            logger_1.loggerWrapper.warn('WARNING: Measurement utils encountered data exceeding maximum allowed decimal precision.' +
                ` Rounding to default max precision. ${JSON.stringify(warningData)}`);
            return roundedValue;
        }
        return value;
    }
    /** @returns unit for this Measurement */
    getUnit() {
        return this._unit;
    }
    /**
     * @deprecated use convertToUnsafeMeasurement().serialize().value if you need a number
     * CAUTION: Take care in any use case separating value from measurement unit
     * @returns value for Measurement as a SmartFloat
     */
    getValue() {
        return this._value;
    }
    /**
     * @returns this Measurement in "safe" serialized JSON form (safe float handling)
     */
    serialize() {
        return {
            value: this._value.toSafeFloat(),
            unit: this._unit
        };
    }
    /**
     * @returns this Measurement in "stringfloat" serialized JSON form (alternate safe float handling)
     */
    serializeWithStringFloat() {
        return {
            value: this._value.toStringFloat(),
            unit: this._unit
        };
    }
    /**
     * @returns whether this measurement and another have the same unit
     * */
    hasSameUnit(measurement) {
        return this._unit === measurement._unit;
    }
    /**
     * _**CAUTION!** Whenever possible, **AVOID "Unsafe" forms** and **prefer "Safe" forms** instead!_
     *
     * Official recommended pathway for converting a (safe) `Measurement` to an `UnsafeMeasurement`,
     * with primary (unfortunate) use case being when some API or library needs a float value
     * in straight `number` form. (Follow with `unsafeMeasurement.serialize()` in this case.)
     *
     * `UnsafeMeasurement` intentionally supports very little functionality compared to the normal/safe
     * `Measurement` tooling. Recommended to only call this at the last minute before serialization to
     * unsafe float number format is needed.
     */
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    convertToUnsafeMeasurement() {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return new UnsafeMeasurement(this._value.toNumber(), this._unit);
    }
    /** @throws if measurements A and B do not have matching units */
    _validateCompatibleMeasurementUnits(measurement, options = {}) {
        if (!this.hasSameUnit(measurement)) {
            const standardErrMsg = 'Measurement Units do not match';
            const standardErrData = { thisMeasurement: this.serialize(), otherMeasurement: measurement.serialize() };
            const errMsg = options.customErrorMessagePrefix
                ? `${options.customErrorMessagePrefix}: ${standardErrMsg}`
                : standardErrMsg;
            throw new error_1.CommonUtilsError(errMsg, standardErrData);
        }
    }
    /**
     * DRY internal method to keep various arithmetic method implementations in sync
     *
     * @param operandInput Other measurement to combine with this measurement via operation
     * @param operationName A name to give the operation in error messages and the like
     * @param operationFunc Operation (e.g. add) implemented as callback
     */
    _executeCalculationWithValidations(operandInput, operationName, operationFunc) {
        const otherMeasurement = new Measurement(operandInput);
        this._validateCompatibleMeasurementUnits(otherMeasurement, {
            customErrorMessagePrefix: `Cannot perform measurement arithmetic operation (${operationName})`
        });
        const result = operationFunc(otherMeasurement);
        return new Measurement({ value: result.serialize(), unit: this._unit });
    }
    /**
     * DRY internal method to keep various comparison method implementations in sync
     *
     * @param comparandInput Other measurement to compare to this measurement
     * @param operationName A name to give the comparison in error messages and the like
     * @param operationFunc Comparison (e.g. isGreaterThan) implemented as callback
     */
    _executeComparisonWithValidations(comparandInput, comparisonName, comparisonFunc) {
        const otherMeasurement = new Measurement(comparandInput);
        this._validateCompatibleMeasurementUnits(otherMeasurement, {
            customErrorMessagePrefix: `Cannot perform measurement comparison (${comparisonName})`
        });
        return comparisonFunc(otherMeasurement);
    }
    /**
     * Adds an input measurement to current measurement, returning a new Measurement unit for sum
     * NOTE: does not mutate this Measurement or input
     * @throws if this value and input do not have same unit
     */
    add(addend) {
        return this._executeCalculationWithValidations(addend, 'ADD', (operand) => this._value.add(operand._value));
    }
    /**
     * Subtracts input measurement from current measurement, returning a new Measurement for difference.
     * NOTE: does not mutate this Measurement or input
     * @throws if this value and input do not have same unit
     */
    subtract(subtrahend) {
        return this._executeCalculationWithValidations(subtrahend, 'SUBTRACT', (operand) => this._value.subtract(operand._value));
    }
    /**
     * Adds together all input measurements, returning a new Measurement for sum.
     * NOTE: does not mutate any input value
     * @throws if input array is empty
     * @throws if some of the inputs do not have matching units
     */
    static sum(measurements) {
        if (measurements.length === 0) {
            throw new error_1.CommonUtilsError('Cannot sum measurements: input array is empty', { measurements });
        }
        let sum = new Measurement(measurements[0]);
        for (let i = 1; i < measurements.length; i++) {
            sum = sum.add(measurements[i]);
        }
        return sum;
    }
    /**
     * @deprecated Deliberately unimplemented until we have pattern for supporting compound units (e.g. meters squared).
     * @throws NOT IMPLEMENTED (see deprecated reason)
     *
     * Multiply current measurement by an input measurement, returning a new Measurement
     * NOTE: does not mutate this Measurement or input
     * @throws if this value and input do not have same unit
     */
    multiply(multiplier) {
        throw new error_1.CommonUtilsError('Multiply feature not implemented pending compound unit support');
        // TODO: turn logic below back on once compount units are supported
        // return this._executeCalculationWithValidations(multiplier, 'MULTIPLY', (operand: Measurement) =>
        //   this._value.multiply(operand._value)
        // );
    }
    /**
     * @deprecated Deliberately unimplemented until we have pattern for supporting compound units (e.g. bushels/acre).
     * @throws NOT IMPLEMENTED (see deprecated reason)
     *
     * Divide current measurement by an input measurement, returning a new Measurement
     * NOTE: does not mutate this Measurement or input
     * @throws if this value and input do not have same unit
     */
    divide(divisor) {
        throw new error_1.CommonUtilsError('Divide feature not implemented pending compound unit support');
        // TODO: turn logic below back on once compount units are supported
        // return this._executeCalculationWithValidations(divisor, 'DIVIDE', (operand: Measurement) =>
        //   this._value.divide(operand._value)
        // );
    }
    /**
     * @returns whether input is greater than this Measurement's value
     * NOTE: does not mutate this Measurement or input values
     * @throws if input does not have same unit as this Measurement
     */
    isGreaterThan(comparand) {
        return this._executeComparisonWithValidations(comparand, 'GT [a > b]', (comparand) => this._value.isGreaterThan(comparand._value));
    }
    /**
     * @returns whether input is greater than or equal to this Measurement's value
     * NOTE: does not mutate this Measurement or input values
     * @throws if input does not have same unit as this Measurement
     */
    isGreaterThanOrEqualTo(comparand) {
        return this._executeComparisonWithValidations(comparand, 'GTE [a >= b]', (comparand) => this._value.isGreaterThanOrEqualTo(comparand._value));
    }
    /**
     * @returns whether input is less than this Measurement's value
     * NOTE: does not mutate this Measurement or input values
     * @throws if input does not have same unit as this Measurement
     */
    isLessThan(comparand) {
        return this._executeComparisonWithValidations(comparand, 'LT [a < b]', (comparand) => this._value.isLessThan(comparand._value));
    }
    /**
     * @returns whether input is less than or equal to this Measurement's value
     * NOTE: does not mutate this Measurement or input values
     * @throws if input does not have same unit as this Measurement
     */
    isLessThanOrEqualTo(comparand) {
        return this._executeComparisonWithValidations(comparand, 'LTE [a <= b]', (comparand) => this._value.isLessThanOrEqualTo(comparand._value));
    }
    /**
     * @returns whether input is equal in value to this Measurement's value
     * NOTE: does not mutate this Measurement or input values
     * @throws if input does not have same unit as this Measurement
     */
    isEqualTo(comparand) {
        return this._executeComparisonWithValidations(comparand, 'EQ [a === b]', (comparand) => this._value.isEqualTo(comparand._value));
    }
    /**
     * @returns whether this measurement's value is equal to zero
     */
    isZero() {
        return this._value.isZero();
    }
    /**
     * Creates new Measurement model based on this Measurements, but with decimal precision converted to match args
     * NOTE: does not mutate this Measurement
     * @throws if requested decimal precision exceeds allowed maximum precision
     */
    toPrecision(decimalPrecision, roundingMode = math_1.RoundingMode.RoundHalfEven) {
        if (!Number.isInteger(decimalPrecision)) {
            throw new error_1.CommonUtilsError('Cannot convert decimal precision on measurement: requested precision must be an integer', {
                measurement: this.serialize(),
                requestedDecimalPrecision: decimalPrecision,
                requestedRoundingMode: roundingMode
            });
        }
        if (decimalPrecision > exports.MAXIMUM_MEASUREMENT_DECIMAL_PRECISION) {
            throw new error_1.CommonUtilsError('Cannot convert Measurement to new precision: Requested decimal precision exceeds allowed maximum', {
                originalMeasurement: this.serialize(),
                requestedDecimalPrecision: decimalPrecision,
                enforcedMaximumDecimalPrecision: exports.MAXIMUM_MEASUREMENT_DECIMAL_PRECISION
            });
        }
        const newPrecisionValue = this._value.toPrecision(decimalPrecision, roundingMode);
        return new Measurement({ value: newPrecisionValue.serialize(), unit: this._unit });
    }
    /**
     * _**CAUTION!** Whenever possible, the standard SerializedMeasurement form is strongly preferred
     * to non-universal, less-flexible legacy forms._
     *
     * Special build method for constructing directly legacy "SafeCropQuantity" form in Marketplace GraphQL APIs.
     * */
    static fromLegacySafeCropQuantity(safeCropQuantity) {
        return new Measurement({ value: safeCropQuantity.quantity, unit: safeCropQuantity.unitOfMeasure });
    }
    /**
     * _**CAUTION!** Whenever possible, the standard SerializedMeasurement form is strongly preferred
     * to non-universal, less-flexible legacy forms._
     *
     * Special serialize method for constructing directly to legacy "SafeCropQuantity" form in Marketplace GraphQL APIs.
     *
     * NOTE: consumer may have to cast the type since this tooling has no knowledge of the GraphQL CropQuantityUnit enum
     * and has to represent the unit as a primitive string type.
     */
    static toLegacySafeCropQuantity(measurement) {
        const { value, unit } = measurement.serialize();
        return { quantity: value, unitOfMeasure: unit };
    }
}
exports.Measurement = Measurement;
/**
 * _**CAUTION!** Whenever possible, **AVOID "Unsafe" forms** and **prefer "Safe" forms** instead!_
 * @see `Measurement` for "safe" alternative
 *
 * Official utility class for handling any *Unsafe* "measurement" values (where "measurement" means a value that must
 * be paired with a unit to have any meaning -- e.g. weight(10kg), crop quantity(5000 bushels), area (2.5ha), etc.)
 *
 * The `UnsafeMeasurement` class is is intentionally minimal in order to
 * deliberately discourage use whenever possible.
 * It is **strongly recommended** to convert instances of `UnsafeMeasurement`
 * to the safe `Measurement` alternative.
 *
 * Valid use cases for the UnsafeMeasurement class revolve around communication with dependencies
 * that cannot be easily modified to use "safe" serialized forms:
 * - ingesting an "unsafe" float number value from external some data source or API, with intent
 *   to immediately convert to safe `Measurement` (**strongly recommended**)
 *   - see `unsafeMeasurement.convertToMeasurement()`
 * - as an intermediate form in converting a safe `Measurement` to an unsafe serialized form when a
 *   straight float `number` is required by some dependency (e.g. downstream API)
 *   - see `measurement.convertToUnsafeMeasurement()` and `unsafeMeasurement.serialize()`
 * */
class UnsafeMeasurement {
    /**
     * _**CAUTION!** Whenever possible, **AVOID "Unsafe" forms** and **prefer "Safe" forms** instead!_
     *
     * Create an `UnsafeMeasurement` instance from "unsafe" serialized currency data.
     *
     * Main purpose: vehicle to convert "unsafe" float number data to a "safe" `Measurement` instance
     * (**strongly recommended to do as soon as possible**).
     *
     * NOTE: This constructor does not validate precision nor integer safety,
     * intentionally allowing for potentially unsafe manipulations within `UnsafeMeasurement`
     * before calling `convertToMeasurement()`.
     */
    constructor(value, unit) {
        this._value = value;
        this._unit = unit;
    }
    /**
     * _**CAUTION!** Whenever possible, **AVOID "Unsafe" forms** and **prefer "Safe" forms** instead!_
     * @see `SerializedMeasurement` for "safe" alternative
     *
     * Official recommended pathway for extracting a float value (and unit) from deserialized
     * `UnsafeMeasurement` form.
     * - Use in combination with `currency.convertToUnsafeMeasurement()` if you need to convert a safe
     *   `Measurement` instance to a straight float value `number`
     *
     * @returns This `UnsafeMeasurement` instance in serialized JSON form
     */
    serialize() {
        return {
            value: this._value,
            unit: this._unit
        };
    }
    /**
     * **STRONGLY RECOMMENDED TO CALL THIS AS EARLY AS POSSIBLE AFTER CONSTRUCTION.**
     *
     * The "safe" Measurement class has lots of useful tooling for arithmetic, comparision, precision, etc.
     *
     * Caveat: "safe" Measurement will involve conversion and can sometimes change the underlying data
     * (e.g. if the data runs afoul of the maximum reasonabl decimal precision).
     *
     * @returns A safe `Measurement` instance representation of this `UnsafeMeasurement`
     * @throws `CommonUtilsError` if this `UnsafeMeasurement` could not be successfully converted to a
     * `Measurement` instance
     */
    convertToMeasurement() {
        const smartFloat = new math_1.SmartFloat(this._value);
        const safeFloat = smartFloat.toSafeFloat();
        return new Measurement({ value: safeFloat, unit: this._unit });
    }
}
exports.UnsafeMeasurement = UnsafeMeasurement;
//# sourceMappingURL=measurement.js.map